import { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import {
  getAnaliseCriteriosLiberacao,
  type GrupoCriterioLiberacao,
} from "../../../../services/avaliacaoSolicitacaoService";

export type LiberarSolicitacaoModalProps = {
  show: boolean;
  solicitacaoId: number | null;
  onClose: () => void;
  onConfirm: (criterioIds: number[]) => void;
  isLoading?: boolean;
};

const LiberarSolicitacaoModal = ({
  show,
  solicitacaoId,
  onClose,
  onConfirm,
  isLoading = false,
}: LiberarSolicitacaoModalProps) => {
  const [grupos, setGrupos] = useState<GrupoCriterioLiberacao[]>([]);
  const [selecionados, setSelecionados] = useState<number[]>([]);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!show || !solicitacaoId) {
      setGrupos([]);
      setSelecionados([]);
      return;
    }

    let cancelled = false;
    setFetching(true);
    getAnaliseCriteriosLiberacao(solicitacaoId)
      .then((data) => {
        if (!cancelled) setGrupos(data.gruposCriterios ?? []);
      })
      .catch(() => {
        if (!cancelled) setGrupos([]);
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, solicitacaoId]);

  const toggle = (id: number) => {
    setSelecionados((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const totalCriterios = grupos.reduce(
    (acc, g) => acc + g.criteriosAvaliacao.length,
    0,
  );

  return (
    <Modal
      show={show}
      onHide={() => {
        if (isLoading) return;
        onClose();
      }}
      centered
      backdrop={isLoading ? "static" : true}
      keyboard={!isLoading}
    >
      <Modal.Header closeButton={!isLoading}>
        <Modal.Title>LIBERAR NOVA SOLICITAÇÃO</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted small mb-3">
          Favor selecione os documentos que deseja liberar para nova solicitação
        </p>

        {fetching && (
          <div className="d-flex justify-content-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {!fetching && totalCriterios === 0 && (
          <p className="text-muted small">
            Nenhum documento disponível para liberação.
          </p>
        )}

        {!fetching &&
          grupos.map((grupo) => (
            <div key={grupo.titulo} className="mb-3">
              <h6 className="text-primary fw-bold mb-2">{grupo.titulo}</h6>
              {grupo.criteriosAvaliacao.map((criterio) => (
                <Form.Check
                  key={criterio.id}
                  type="checkbox"
                  id={`liberar-criterio-${criterio.id}`}
                  label={criterio.titulo}
                  checked={selecionados.includes(criterio.id)}
                  onChange={() => toggle(criterio.id)}
                  className="mb-2"
                />
              ))}
            </div>
          ))}
      </Modal.Body>

      <Modal.Footer className="border-0 merit-confirm-footer">
        <Button
          type="button"
          variant="outline-secondary"
          className="merit-confirm-btn-nao"
          onClick={onClose}
          disabled={isLoading}
        >
          CANCELAR
        </Button>
        <Button
          type="button"
          variant="primary"
          className="merit-confirm-btn-sim"
          onClick={() => onConfirm(selecionados)}
          disabled={isLoading || fetching || selecionados.length === 0}
        >
          {isLoading ? "LIBERANDO..." : "LIBERAR SOLICITAÇÃO"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LiberarSolicitacaoModal;
